import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ProcessingJobItem } from '../../web-api-client';

export type ThroughputPeriod = 'day' | 'week' | 'month';

interface ThroughputPoint {
  time: string;
  processed: number;
  failed: number;
}

const formatSlot = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;

export const buildThroughputSeries = (
  jobs: ProcessingJobItem[],
  period: ThroughputPeriod = 'day',
  now = new Date(),
  locale = 'en',
): ThroughputPoint[] => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period !== 'day') {
    start.setDate(start.getDate() - (period === 'week' ? 6 : 29));
  }
  const count = period === 'day' ? 24 : period === 'week' ? 7 : 30;
  const dates = Array.from({ length: count }, (_, index) => {
    const date = new Date(start);
    if (period === 'day') date.setHours(index);
    else date.setDate(date.getDate() + index);
    return date;
  });
  const buckets: ThroughputPoint[] = dates.map((date) => ({
    time: period === 'day' ? formatSlot(date.getHours()) : date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
    processed: 0,
    failed: 0,
  }));

  jobs.forEach((job) => {
    const timestamp = job.completedAt ?? job.deadLetteredAt ?? job.startedAt;
    if (!timestamp) {
      return;
    }

    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (Number.isNaN(date.getTime()) || date < start || date > now) {
      return;
    }

    const index = period === 'day' ? date.getHours() : dates.findIndex((day) =>
      day.getFullYear() === date.getFullYear() && day.getMonth() === date.getMonth() && day.getDate() === date.getDate());
    const bucket = buckets[index];
    if (!bucket) return;
    if (job.jobStatus === 'Failed' || job.jobStatus === 'DeadLettered') {
      bucket.failed += 1;
      return;
    }

    if (job.jobStatus === 'Succeeded') {
      bucket.processed += 1;
    }
  });

  return buckets;
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label }) => {
  const theme = useTheme();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      style={{
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.common.white,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0px 4px 12px rgba(16, 24, 40, 0.08)',
      }}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.name}
          style={{
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: theme.palette.text.secondary,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: entry.color,
              display: 'inline-block',
            }}
            aria-hidden
          />
          {entry.name}:{' '}
          <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

interface ThroughputChartProps {
  jobs: ProcessingJobItem[];
  period: ThroughputPeriod;
}

const ThroughputChart: React.FC<ThroughputChartProps> = ({ jobs, period }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const data = useMemo(() => buildThroughputSeries(jobs, period, new Date(), i18n.language), [jobs, period, i18n.language]);

  const axis = {
    fill: theme.palette.text.secondary,
    fontSize: 11,
  };

  return (
    <div style={{ height: 220, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.palette.divider}
            vertical={false}
          />
          <XAxis
            dataKey="time"
            tick={axis}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
          />
          <YAxis tick={axis} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: theme.palette.primary[50] }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="processed"
            name={t('dashboard.processed')}
            fill={theme.palette.primary.main}
            radius={[3, 3, 0, 0]}
          />
          <Bar
            dataKey="failed"
            name={t('dashboard.failed')}
            fill={theme.palette.error.main}
            radius={[3, 3, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ThroughputChart;
